<template>
<div v-bind:class="{ 'loadingIndicator': loadingIndicator, 'hasSOTW': hasSOTW }">
    <div v-if="offline" class="ui floating warning message" style="opacity:.8; position:fixed; top:0; left:20px; z-index:111;">{{ 'You seem to be offline' | translate }}</div>

    <model-sellers v-if="$root.authCompleted" ref="modelSellers"/>

    <!-- First-run sync modal: shown when user lands on dashboard after signup + seller account connect -->
    <first-run-sync-modal
      v-if="showFirstRunSyncModal"
      :user-email="$root.userEmail"
      :onboarding-status="onboardingStatus"
      :on-dismiss="onFirstRunSyncDismiss"
    />

    <transition :name="transitionName" :mode="transitionMode">
    <!-- keep-alive include="home" -->
      <router-view
        v-on:sellerAccountsUpdated="$refs.modelSellers.updateSellers()" />
    <!-- /keep-alive -->
    </transition>

    <div class="stts21kdfslds"></div>

    <product-details-chart-modal ref="chartModal" />

    <div id="errorModal" class="ui modal">
      <i class="close icon"></i>
      <div class="header">{{ 'Error' | translate }}</div>
      <div class="ui content">
        <div class="description">{{errorMessage}}</div>
      </div>
      <div class="actions">
        <div class="ui black deny button">{{ 'Close' | translate }}</div>
      </div>
    </div>

    <div id="manyAsinsModal" class="ui modal">
      <i class="close icon"></i>
      <div class="header">Light UI (beta) available</div>
      <div class="ui content">
        <div class="description">Lot's of products to show. You might benefit from our new Light UI. Simpler, snappier.<br/>And you can always switch back just by reloading the page.</div>
      </div>
      <div class="actions">
        <div class="ui grey deny button">No, Continue</div>
        <div class="ui positive button">Try out Light UI</div>
      </div>
    </div>

    <div id="loginModal" class="ui modal">
      <div class="header">Log-in</div>
      <div class="ui content">
        <div v-if="loginError" class="ui error message">
          <div>{{loginError}}</div>
        </div>
        <div class="ui form">
          <div class="ui field">
            <label>{{ 'Email' | translate }}</label>
            <input type="text" v-model="email" placeholder="Email" required="true" readonly="readonly" />
          </div>
          <div class="ui field">
            <label>{{ 'Password' | translate }}</label>
            <input type="password" v-model="password" autofocus="true" required="true" v-on:keydown.enter="login" />
          </div>
          <div class="ui field">
            <div class="ui checkbox">
              <input type="checkbox" data-lpignore="true" v-model="keepMeSignedIn">
              <label>{{ 'Keep me signed in' | translate }}</label>
            </div>
          </div>
        </div>
      </div>
      <div class="actions">
        <button class="ui primary ok button" type="submit">{{ 'Log in' | translate }}</button>
      </div>
    </div>

    <transition name="fadeDown">
    <div v-show="announcement!=null&&announcement!=closedAnnouncementText" class="ui orange message SKannouncement">
      <i class="close icon" @click="closedAnnouncementText=announcement"></i>
      <strong>[Announcement]</strong> &nbsp;&nbsp;&nbsp;&nbsp; {{announcement}}
    </div>
    </transition>

    <transition name="fadeDown">
    <div v-show="accountErrors!=null&&!accountErrorsTextClosed" class="ui red message SKannouncement">
      <i class="close icon" @click="accErrorBoxClose"></i>
      <strong>[IMPORTANT]</strong> &nbsp;&nbsp;&nbsp;&nbsp; {{accountErrors}}
    </div>
    </transition>

</div>
</template>

<script>
import Vue from 'vue'
import $ from 'jquery'
import ModelSellers from './components/Model.Sellers'
import ProductDetailsChartModal from './components/ProductDetailsChartModal'
import FirstRunSyncModal from './components/FirstRunSyncModal.vue'
import { clearConnectDeferredHint, readConnectDeferredHint, setPipelineDoneHint } from './utils/onboardingSessionHints.js'

export default {
  components: {
    ModelSellers,
    ProductDetailsChartModal,
    FirstRunSyncModal,
  },
  data: function () {
    const apiVersion = 5

    // world store
    const wdList = ['badReview', 'topReview', 'starRatingRecentChange', 'numberOfSellers', 'lostBuybox', 'noAddToCart', 'price', 'salesDrop', 'imageMissing', 'productDetailsRecentChange', 'productCategoryRecentChange', 'bestSellerBadge', 'badSellerFeedback', 'listingSuspended', 'searchBan', 'inventory', 'fbmInventory', 'fbaFeeChange', 'prime', 'amazonChoiceBadge', 'subscribeAndSave', 'parentProductChange', 'adultClassification', 'competitorPrice', 'competitivePrice', 'reviewHealth']
    const worldStore = { 'state': null, 'sellerAccounts': { }, 'products': { }, 'watchDogs': { }, 'reds': {}, 'ambers': {}, 'currentVersion': apiVersion, 'minVersion': apiVersion, 'announcement': null }
    for (let n in wdList) worldStore.watchDogs[wdList[n]] = { 'status': 0, 'alertsEnabled': null, 'message': '', 'settings': {}, 'items': {} }

    // onboarding notes
    let onboarding = {wdtabs: true, issuestatus: true, viewtype: true, mainmenu: true, desktopnotif: true, saleschrt: true, dtntfy: true, syncing: true}
    for (let n in onboarding) onboarding[n] = (localStorage.getItem('onb' + n) === null)
    // if the user has set his Desktop Notification preference already disable that note
    if ('Notification' in window && Notification.permission !== 'default') onboarding.dtntfy = false

    return {
      apiVersion: apiVersion,
      email: null,
      errorMessage: null,
      password: null,
      keepMeSignedIn: false,
      loginNeeded: false,
      loginModalElement: null,
      loginError: null,
      offline: false,
      transitionName: '',
      transitionMode: 'out-in',
      worldStore: worldStore,
      wdList: wdList,
      hasSOTW: false, // used only for CSS
      sotwi: null,
      // Stocks pagination state
      stocksCurrentPage: 0,
      stocksPageSize: 20,
      stocksTotalItems: 0,
      stocksTotalPages: 0,
      stocksSummary: null,
      stockSummaryRequestId: 0,
      /** Ordered product IDs from the latest stock API response — single source of truth for StockPage. */
      stockPageProductIds: [],
      loadingIndicator: false,
      announcement: null,
      closedAnnouncementText: null,
      accountErrors: null,
      accountErrorsTextClosed: false,
      onboarding: onboarding,
      stateChangeRequestInFlight: new Set(),
      manyAsinsModalShowed: false,
      manyAsinsModalClosed: false,
      firstSotw: true,
      onboardingStatus: {
        sellerConnected: false,
        productsImported: false,
        buyboxFetched: false,
        ordersFetched: false,
        completed: false,
        completedAt: null
      },
      onboardingPollTimer: null,
      /** Cleared when onboarding GET completes; unblocks UI if request hangs. */
      _onboardingReadyTimeoutId: null,
      /** First `/api/v1/onboarding/status` response received (avoids empty-state flash on dashboard). */
      onboardingStatusReady: false,
    }
  },
  methods: {
    onFirstRunSyncDismiss: function () {
      this.onboarder('syncing')
    },
    onboarder: function (id) {
      $('[data-onboarding=' + id + ']').hide(400)
      setTimeout(() => { this.onboarding[id] = false }, 999)
      localStorage.setItem('onb' + id, 'y')
      if (id === 'dtntfy' && 'Notification' in window) Notification.requestPermission()
    },
    accErrorBoxClose: function () {
      this.accountErrorsTextClosed = true
      setTimeout(() => { this.accountErrorsTextClosed = false }, 1000 * 60 * 60 * 3) // alert comes back after 3 hours
    },
    fetchOnboardingStatus: function (forceRefresh) {
      var self = this
      if (self.onboardingStatus && self.onboardingStatus.completed) {
        self.onboardingStatusReady = true
        return
      }
      var s0 = self.onboardingStatus
      var alreadyAllSteps = s0 && !!s0.sellerConnected && !!s0.productsImported && !!s0.buyboxFetched && !!s0.ordersFetched
      if (alreadyAllSteps) {
        self.onboardingStatusReady = true
        return
      }
      if (self._onboardingReadyTimeoutId) {
        clearTimeout(self._onboardingReadyTimeoutId)
        self._onboardingReadyTimeoutId = null
      }
      self._onboardingReadyTimeoutId = setTimeout(function () {
        self._onboardingReadyTimeoutId = null
        if (!self.onboardingStatusReady) {
          self.onboardingStatusReady = true
        }
      }, 15000)
      var force = !!forceRefresh
      var statusPromise = self.$root.loadOnboardingStatus
        ? self.$root.loadOnboardingStatus(force)
        : self.$http.get('/api/v1/onboarding/status').then(function (response) { return response.body || {} }, function () { return null })
      statusPromise.then(
        function (data) {
          if (self._onboardingReadyTimeoutId) {
            clearTimeout(self._onboardingReadyTimeoutId)
            self._onboardingReadyTimeoutId = null
          }
          data = data || {}
          self.onboardingStatusReady = true
          self.onboardingStatus = {
            sellerConnected: !!data.sellerConnected,
            productsImported: !!data.productsImported,
            buyboxFetched: !!data.buyboxFetched,
            ordersFetched: !!data.ordersFetched,
            completed: !!data.completed,
            completedAt: data.completedAt || null
          }
          var pipelineDone = !!data.completed || (!!data.sellerConnected && !!data.productsImported && !!data.buyboxFetched && !!data.ordersFetched)
          if (pipelineDone) {
            setPipelineDoneHint()
          }
          if (data.sellerConnected) {
            clearConnectDeferredHint()
          }
          if (pipelineDone && self.onboardingPollTimer) {
            clearInterval(self.onboardingPollTimer)
            self.onboardingPollTimer = null
          }
          if (pipelineDone && self.onboarding.syncing) {
            self.onboarding.syncing = false
            localStorage.setItem('onbsyncing', 'y')
          }
        },
        function () {
          if (self._onboardingReadyTimeoutId) {
            clearTimeout(self._onboardingReadyTimeoutId)
            self._onboardingReadyTimeoutId = null
          }
          self.onboardingStatusReady = true
        }
      )
    },
    startOnboardingPoll: function () {
      this.fetchOnboardingStatus(false)
      var self = this
      this.onboardingPollTimer = setInterval(function () {
        var st = self.onboardingStatus
        var done = st && (st.completed || (!!st.sellerConnected && !!st.productsImported && !!st.buyboxFetched && !!st.ordersFetched))
        if (done) {
          clearInterval(self.onboardingPollTimer)
          self.onboardingPollTimer = null
          return
        }
        self.fetchOnboardingStatus(true)
      }, 60000)
    },
    signOut: function () {
      clearInterval(this.sotwi) // stop polling the server
      this.sotwi = null
      this.$root.signOutUser()
    },
    goSettings: function () { this.$router.push('/settings') },
    login: function () {
      this.$http.post('/api/auth/login/standard', {
        email: this.email,
        password: this.password,
        keepMeSignedIn: this.keepMeSignedIn
      }).then((response) => {
        this.password = null
        this.loginError = null
        this.loginNeeded = false
        this.loginModalElement.modal('hide')
      }, (response) => {
        this.loginError = response.body.message
      })
      return false
    },
    hasInFlightStateChangeRequest: function () {
      const threshold = Date.now() - 30000
      for (let value of this.stateChangeRequestInFlight.values()) {
        if (value > threshold) return true
      }
      return false
    },
    /** Legacy SOTW poller disabled — v1 APIs load page data. Keep stubs for old call sites. */
    updateSotwPolling: function () {
      if (this.sotwi) {
        clearInterval(this.sotwi)
        this.sotwi = null
      }
    },
    stateOfTheWorld: function () {
      this.loadingIndicator = false
    },
    dashboardStats: function () {
      console.log('[IndexPage] dashboardStats called - COMMENTED OUT')
      // COMMENTED OUT: stateoftheworld API call
      return
      /* try {
        // DON'T show full-page loader for dashboard stats (prevents twitching)
        // DON'T reset stats to zeros (prevents showing 0 0 0 0)

        this.$http.get('/api/sentinel/stateoftheworld', {
          params: { statsOnly: true }
        }).then((response) => {
          console.log('[IndexPage] dashboardStats API response:', response.body)
          try {
            if (typeof response.body === 'string' || response.body instanceof String) return

            // Store stats globally for Dashboard component to access
            console.log('[IndexPage] Storing dashboard stats globally:', response.body)
            this.$root.dashboardStats = response.body
            // Emit event to notify Dashboard component
            this.$root.$emit('dashboard-stats-loaded', response.body)
          } catch (error) {
            console.error('[IndexPage] Error processing dashboard stats:', error)
            window.onerror(error.message || 'N/A', error.url || 'N/A', error.lineNumber || 'N/A', error.columnNumber || 'N/A', error.error || error.name || 'N/A')
          }
        }, (response) => {
          console.error('[IndexPage] dashboardStats API error:', response)
        })
      } catch (error) {
        console.error('[IndexPage] dashboardStats method error:', error)
        window.onerror(error.message || 'N/A', error.url || 'N/A', error.lineNumber || 'N/A', error.columnNumber || 'N/A', error.error || error.name || 'N/A')
      } */
    },
    productsList: function (marketplaces, sellerAccounts) {
      try {
        const params = { productsOnly: true }
        if (marketplaces && String(marketplaces).trim() !== '') params.marketplaces = marketplaces
        if (sellerAccounts && String(sellerAccounts).trim() !== '') params.sellerAccounts = sellerAccounts
        this.$http.get('/api/sentinel/products', {
          params: params
        }).then((response) => {
          try {

            if (typeof response.body === 'string' || response.body instanceof String) {
              console.warn('[IndexPage] Response body is a string, skipping')
              return
            }

            // Normalize and enrich products with required fields
            if (response.body.products) {
              Object.keys(response.body.products).forEach(productId => {
                const product = response.body.products[productId]
                // Normalize marketplace
                if (product.marketplace) {
                  product.marketplace = product.marketplace.toUpperCase()
                }
                if (!product.productGroupId) {
                  product.productGroupId = productId
                }
                if (!product.productGroupProductIds) {
                  product.productGroupProductIds = [productId]
                }
                if (product.ageHours === undefined) {
                  if (product.salesYesterday !== undefined || product.salesToday !== undefined) {
                    product.ageHours = 0
                  } else {
                    product.ageHours = -1
                  }
                }
                if (product.starRating === undefined) product.starRating = product.rating != null ? product.rating : null
                if (product.bsrRank === undefined) product.bsrRank = product.salesRank || null
                if (product.salesYesterdayDate === undefined) {
                  if (product.lastUpdated) {
                    const date = new Date(product.lastUpdated)
                    product.salesYesterdayDate = date.getDate()
                    product.salesYesterdayMonth = date.getMonth() + 1
                  } else {
                    product.salesYesterdayDate = null
                    product.salesYesterdayMonth = null
                  }
                }
                if (product.salesTodayDate === undefined) {
                  if (product.lastUpdated) {
                    const date = new Date(product.lastUpdated)
                    product.salesTodayDate = date.getDate()
                    product.salesTodayMonth = date.getMonth() + 1
                  } else {
                    product.salesTodayDate = null
                    product.salesTodayMonth = null
                  }
                }
              })
            }

            // Convert reds and ambers from product objects to counts by productGroupId
            const redsByGroupId = {}
            const ambersByGroupId = {}

            if (response.body.reds) {
              Object.keys(response.body.reds).forEach(productId => {
                // Get productGroupId from the main products object if available
                const mainProduct = response.body.products && response.body.products[productId]
                const groupId = (mainProduct && mainProduct.productGroupId) || productId
                redsByGroupId[groupId] = (redsByGroupId[groupId] || 0) + 1
              })
            }

            if (response.body.ambers) {
              Object.keys(response.body.ambers).forEach(productId => {
                // Get productGroupId from the main products object if available
                const mainProduct = response.body.products && response.body.products[productId]
                const groupId = (mainProduct && mainProduct.productGroupId) || productId
                ambersByGroupId[groupId] = (ambersByGroupId[groupId] || 0) + 1
              })
            }

            if (response.body.products) {
              Object.keys(this.worldStore.products).forEach(key => {
                this.$delete(this.worldStore.products, key)
              })
              Object.keys(response.body.products).forEach(productId => {
                this.$set(this.worldStore.products, productId, response.body.products[productId])
              })
            }
            Object.keys(this.worldStore.reds).forEach(key => {
              this.$delete(this.worldStore.reds, key)
            })
            Object.keys(redsByGroupId).forEach(groupId => {
              this.$set(this.worldStore.reds, groupId, redsByGroupId[groupId])
            })
            console.log('[IndexPage] Updating ambers:', Object.keys(ambersByGroupId).length, 'product groups')
            Object.keys(this.worldStore.ambers).forEach(key => {
              this.$delete(this.worldStore.ambers, key)
            })
            Object.keys(ambersByGroupId).forEach(groupId => {
              this.$set(this.worldStore.ambers, groupId, ambersByGroupId[groupId])
            })
            if (response.body.sellerAccounts) {
              Object.keys(response.body.sellerAccounts).forEach(sellerId => {
                this.$set(this.worldStore.sellerAccounts, sellerId, response.body.sellerAccounts[sellerId])
              })
            }
          } catch (error) {
            window.onerror(error.message || 'N/A', error.url || 'N/A', error.lineNumber || 'N/A', error.columnNumber || 'N/A', error.error || error.name || 'N/A')
          }
        }, (response) => {
          if (response.status === 0 || response.status === 502 || response.status === 503 || response.status === 504) {
            import('./services/mockProductsData.js').then(({ mockProductsService }) => {
              mockProductsService.getProducts().then((data) => {
                try {
                  if (data.products) {
                    this.worldStore.products = data.products
                  }
                  if (data.sellerAccounts) {
                    this.worldStore.sellerAccounts = data.sellerAccounts
                  }
                } catch (error) {
                  console.error('Error loading mock products:', error)
                }
              })
            }).catch((error) => {
              console.error('Failed to load mock products:', error)
            })
          }
        })
      } catch (error) {
        window.onerror(error.message || 'N/A', error.url || 'N/A', error.lineNumber || 'N/A', error.columnNumber || 'N/A', error.error || error.name || 'N/A')
      }
    },
    watchdogsData: function () {
      // COMMENTED OUT: stateoftheworld API call
      return
      /* try {
        this.$http.get('/api/sentinel/stateoftheworld', {
          params: { watchdogsOnly: true }
        }).then((response) => {
          try {
            if (typeof response.body === 'string' || response.body instanceof String) return

            // Update watchdog data
            if (response.body.watchDogs) {
              this.worldStore.watchDogs = response.body.watchDogs
            }

            // Update minimal product data
            if (response.body.products) {
              // Merge with existing products, keeping any existing detailed data
              for (const [productId, minimalProduct] of Object.entries(response.body.products)) {
                if (!this.worldStore.products[productId]) {
                  this.worldStore.products[productId] = minimalProduct
                } else {
                  // Update only the minimal fields, preserve existing detailed data
                  const existingProduct = this.worldStore.products[productId]
                  existingProduct.productTitle = minimalProduct.productTitle
                  existingProduct.imageUrl = minimalProduct.imageUrl
                  existingProduct.largeImageUrl = minimalProduct.largeImageUrl
                  existingProduct.marketplace = minimalProduct.marketplace
                  existingProduct.productUrl = minimalProduct.productUrl
                  existingProduct.productReviewsUrl = minimalProduct.productReviewsUrl
                  existingProduct.productTopReviewsUrl = minimalProduct.productTopReviewsUrl
                  existingProduct.productOffersUrl = minimalProduct.productOffersUrl
                  existingProduct.productGroupId = minimalProduct.productGroupId
                  existingProduct.productGroupProductIds = minimalProduct.productGroupProductIds
                }
              }
            }
          } catch (error) {
            window.onerror(error.message || 'N/A', error.url || 'N/A', error.lineNumber || 'N/A', error.columnNumber || 'N/A', error.error || error.name || 'N/A')
          }
        }, (response) => {
          // Handle error silently
        })
      } catch (error) {
        window.onerror(error.message || 'N/A', error.url || 'N/A', error.lineNumber || 'N/A', error.columnNumber || 'N/A', error.error || error.name || 'N/A')
      } */
    },
    normalizeStockSummary: function (raw) {
      if (!raw || typeof raw !== 'object') return null
      var total = raw.totalProducts != null ? raw.totalProducts : raw.total_products
      var healthy = raw.healthyStock != null ? raw.healthyStock : raw.healthy_stock
      var low = raw.lowStockWarning != null ? raw.lowStockWarning : raw.low_stock_warning
      var out = raw.outOfStock != null ? raw.outOfStock : raw.out_of_stock
      var pct = raw.healthyPercentage != null ? raw.healthyPercentage : raw.healthy_percentage
      return {
        totalProducts: Number(total) || 0,
        healthyStock: Number(healthy) || 0,
        lowStockWarning: Number(low) || 0,
        outOfStock: Number(out) || 0,
        healthyPercentage: Number(pct) || 0
      }
    },
    stockData: function (page, pageSize, search, status, marketplaces, sellerAccounts, sort, order, skipSummary) {
      try {
        // Use current page if no page specified
        const currentPage = page !== undefined ? page : this.stocksCurrentPage
        const currentPageSize = pageSize || this.stocksPageSize

        const params = { page: currentPage, pageSize: currentPageSize }
        if (search) params.search = search
        if (status && status !== 'all') {
          params.status = status === 'critical' ? 'critical' : status
        }
        if (marketplaces) params.marketplaces = marketplaces
        if (sellerAccounts) params.sellerAccounts = sellerAccounts
        if (sort) params.sort = sort
        if (order) params.order = order

        if (!skipSummary && currentPage === 0 && (!status || status === 'all')) {
          this.stockSummaryRequestId++
          this.stocksSummary = {
            totalProducts: 0,
            healthyStock: 0,
            lowStockWarning: 0,
            outOfStock: 0,
            healthyPercentage: 0
          }
        }

        this.$http.get('/api/sentinel/stock', { params }).then((response) => {
          try {
            if (typeof response.body === 'string' || response.body instanceof String) return

            const body = response.body

            if (body.pagination) {
              this.stocksTotalItems = body.pagination.totalItems || 0
              this.stocksTotalPages = body.pagination.totalPages || 0
              this.stocksCurrentPage = body.pagination.currentPage || 0
              if (body.pagination.pageSize !== undefined) this.stocksPageSize = body.pagination.pageSize
            }

            if (body.summary) {
              this.stocksSummary = this.normalizeStockSummary(body.summary)
            } else if ((!status || status === 'all') && body.pagination && (body.pagination.totalItems || 0) === 0) {
              this.stocksSummary = {
                totalProducts: 0,
                healthyStock: 0,
                lowStockWarning: 0,
                outOfStock: 0,
                healthyPercentage: 0
              }
            }

            // Remove previous stock-page products from worldStore using the tracked ID list
            const prevIds = this.stockPageProductIds || []
            prevIds.forEach(function (productId) {
              this.$delete(this.worldStore.products, String(productId))
            }.bind(this))

            // Map products from stock API response and track their IDs in order
            const products = body.products
            const newStockProductIds = []
            if (products && Array.isArray(products)) {
              for (const stockProduct of products) {
                const product = Object.assign({}, stockProduct)
                const pid = (product.productId != null && product.productId !== '')
                  ? product.productId
                  : product.product_id
                if (pid === undefined || pid === null || pid === '') continue
                product.productId = pid
                const key = String(pid)

                product.productTitle = product.productName || product.productTitle
                product.totalQty = product.fbaStock
                product.inStockQty = product.fbaAvailable
                product.inboundQty = product.fbaInbound
                product.fbmQty = product.fbmStock
                product.reservedStock = product.reservedStock != null ? product.reservedStock : product.reservedQty
                product.fbaTransferring = product.fbaTransferring != null ? product.fbaTransferring : product.pendingTransshipmentQty
                product.velocity = product.velocity
                product.trend = product.trend
                product.daysLeft = product.daysLeft
                product.daysLeftDisplay = product.daysLeftDisplay
                product.daysLeftSubtitle = product.daysLeftSubtitle
                product.status = product.status
                product.healthStatus = product.healthStatus
                product.avgDailySales = product.velocity

                if (product.asin && !product.productUrl) {
                  product.productUrl = `https://www.amazon.com/dp/${product.asin}`
                }

                this.$set(this.worldStore.products, key, product)
                newStockProductIds.push(key)
              }
            }
            this.stockPageProductIds = newStockProductIds

            if (!skipSummary && (!status || status === 'all') && !body.summary && body.pagination && (body.pagination.totalItems || 0) > 0 && currentPage === 0) {
              this.stockSummaryData(marketplaces, sellerAccounts)
            }
          } catch (error) {
            window.onerror(error.message || 'N/A', error.url || 'N/A', error.lineNumber || 'N/A', error.columnNumber || 'N/A', error.error || error.name || 'N/A')
          }
        }, (response) => {
          console.error('[IndexPage] stockData failed:', response.status, response)
        })
      } catch (error) {
        window.onerror(error.message || 'N/A', error.url || 'N/A', error.lineNumber || 'N/A', error.columnNumber || 'N/A', error.error || error.name || 'N/A')
      }
    },
    stockSummaryData: function (marketplaces, sellerAccounts) {
      try {
        var requestId = ++this.stockSummaryRequestId
        this.stocksSummary = {
          totalProducts: 0,
          healthyStock: 0,
          lowStockWarning: 0,
          outOfStock: 0,
          healthyPercentage: 0
        }
        const params = {}
        if (marketplaces) params.marketplaces = marketplaces
        if (sellerAccounts) params.sellerAccounts = sellerAccounts
        this.$http.get('/api/sentinel/stock/summary', { params }).then((response) => {
          try {
            if (requestId !== this.stockSummaryRequestId) return
            if (typeof response.body === 'string' || response.body instanceof String) return

            const body = response.body
            if (body) {
              this.stocksSummary = this.normalizeStockSummary(body) || this.stocksSummary
            }
          } catch (error) {
            window.onerror(error.message || 'N/A', error.url || 'N/A', error.lineNumber || 'N/A', error.columnNumber || 'N/A', error.error || error.name || 'N/A')
          }
        }, (response) => {
          console.error('[IndexPage] stockSummaryData failed:', response.status, response)
        })
      } catch (error) {
        window.onerror(error.message || 'N/A', error.url || 'N/A', error.lineNumber || 'N/A', error.columnNumber || 'N/A', error.error || error.name || 'N/A')
      }
    },
    // Stocks pagination methods
    loadStocksPage: function (page) {
      this.stocksCurrentPage = page
      this.stockData(page, this.stocksPageSize)
    },
    nextStocksPage: function () {
      if (this.stocksCurrentPage < this.stocksTotalPages - 1) {
        this.loadStocksPage(this.stocksCurrentPage + 1)
      }
    },
    prevStocksPage: function () {
      if (this.stocksCurrentPage > 0) {
        this.loadStocksPage(this.stocksCurrentPage - 1)
      }
    },
    setIssueStatus: function (watchDogId, val) {
      // set the attrib in the store not locally so it displays everywhere else
      const watchDogs = Object.values(this.worldStore.watchDogs)
      for (let i = 0; i < watchDogs.length; ++i) {
        const wd = watchDogs[i]
        if (wd.hasOwnProperty('items')) {
          const items = Object.values(wd.items)
          for (let j = 0; j < items.length; ++j) {
            const item = items[j]
            if (Array.isArray(item)) {
              for (let k = 0; k < item.length; ++k) {
                const item0 = item[k]
                if (item0.hasOwnProperty('watchDogId') && item0.watchDogId === watchDogId) {
                  item0.issueStatus = val
                }
              }
            } else if (item.hasOwnProperty('watchDogId') && item.watchDogId === watchDogId) {
              item.issueStatus = val
            }
          }
        }
      }

      const timestamp = Date.now()
      this.stateChangeRequestInFlight.add(timestamp)
      this.$http.post('/api/sentinel/watchdog/set-issue-status/' + watchDogId + '/' + val, { }).then(
        (response) => {
          this.stateChangeRequestInFlight.delete(timestamp)
        },
        (response) => {
          this.stateChangeRequestInFlight.delete(timestamp)
        })

      this.onboarder('issuestatus')
    },
    setKeywordIssueStatus: function (productId, i, val) {
      // set the attrib in the store not locally so it displays everywhere else
      this.worldStore.watchDogs['keyword'].items[productId][i].issueStatus = val

      this.$http.post('/api/sentinel/watchdog/set-issue-status/' + this.worldStore.watchDogs['keyword'].items[productId][0].watchDogId + '/' + val, { }).then((response) => true, (response) => false)
    },
    log: function (msg) {
      if (typeof console === 'object' && typeof console.log === 'function') {
        console.log(msg)
      }
    }
  },
  mounted: function () {
    $('#preloadr').fadeOut(600)

    this.loginModalElement = $('#loginModal')
    this.loginModalElement.modal({
      closable: false,
      onDeny: () => false,
      onApprove: this.login
    })
    $('#userMenu .dropdown').dropdown({'action': 'hide'})

    /* if (document.cookie.search('uliprecheck=1') !== -1) {
      $('#preloadr').delay(300).fadeOut(600)
    } else {
      $('#preloadr').hide()
    } */
    // if user already OK-ed the desktop notification Onboarding Note, but the browser permissions
    // are on default again (had been reset) then silently ask for the permission again
    if ('Notification' in window && !this.onboarding.dtntfy && Notification.permission === 'default') Notification.requestPermission()
    this.startOnboardingPoll()
  },
  created: function () {
    Vue.http.interceptors.push((request, next) => {
      if (this.loginNeeded && request.url !== '/api/auth/login/standard') {
        this.log('Login needed, suppressing request ' + request.method + ' ' + request.url)
        return
      }
      next((response) => {
        if (response.url === '/api/auth/login/standard') return
        if (response.url === '/api/auth/isauthenticated') return
        if (response.url === '/api/auth/changepwd') return
        if (response.url === '/api/identity/package') return
        if (response.url === '/api/billing/subscription/create') return
        if (response.url === '/api/billing/source') return
        if (response.url === '/api/v1/onboarding/status') return
        if (response.url === '/api/support/ticket') return
        if (response.url && response.url.startsWith('/api/product/asin/exists/')) return
        if (response.status === 304 && response.url && (response.url.startsWith('/api/sentinel/stateoftheworld') || response.url === '/api/auth/extend')) return
        if (this.$root.offline) return
        if (response.status === 429) return
        if (response.status === 401 && response.headers && response.headers.map && Object.keys(response.headers.map).filter(k => k.toLowerCase() === 'www-authenticate').length > 0) {
          const key = Object.keys(response.headers.map).filter(k => k.toLowerCase() === 'www-authenticate')[0]
          const value = response.headers.map[key]
          if (this.email == null || Array.isArray(value) && value.length > 0 && value[0] === 'redirect') {
            this.$router.push('/login')
          } else {
            this.loginNeeded = true
          }
        } else if (!response.ok) {
          // Commented out to prevent error modal from showing
          // this.errorMessage = response.body.message
          // $('#errorModal').modal('show')
        }
      })
    })
    this.$router.beforeEach((to, from, next) => {
      if (this.offline) return // if offline: block page change
      if (from.path === '/home') this.transitionName = 'fromHome'
      else if (to.path === '/home') this.transitionName = 'toHome'
      else this.transitionName = 'fadeLeft'
      this.transitionMode = 'out-in'
      next()
    })
    // load World: load local storage if it exist, call server, set refresh at 1 minute
    const worldStoreTemp = JSON.parse(sessionStorage.getItem('worldStore'))
    if (this.$route.path !== '/table' && worldStoreTemp !== null) {
      if (Object.keys(worldStoreTemp).indexOf('currentVersion') >= 0 &&
          Object.keys(worldStoreTemp).indexOf('userEmail') >= 0 &&
          // worldStoreTemp.userEmail === this.$root.userEmail &&
          worldStoreTemp.currentVersion === this.apiVersion) {
        worldStoreTemp.state = null
        this.worldStore = worldStoreTemp
        this.hasSOTW = true
        // console.log('loaded sessionStorage')
      } else {
        // console.log('removed sessionStorage', worldStoreTemp.userEmail, this.$root.userEmail)
        sessionStorage.removeItem('worldStore')
      }
    }
    this.$root._skIndexShellForDashboard = this
    // World tester:
    // setInterval(() => { this.worldStore.watchDogs.listingSuspended.items[0].status = Math.round(Math.random()); this.worldStore.watchDogs.listingSuspended.items[0].muted = Math.round(Math.random()) }, 1000)
  },
  computed: {
    /** Same idea as Welcome: API may omit completed while all steps are true — never block /home with the modal then. */
    onboardingPipelineComplete: function () {
      var s = this.onboardingStatus
      if (!s) return false
      return !!s.completed ||
        (!!s.sellerConnected && !!s.productsImported && !!s.buyboxFetched && !!s.ordersFetched)
    },
    showFirstRunSyncModal: function () {
      return false
    }
  },
  watch: {
    loginNeeded: function (val) {
      if (!val) return
      this.email = this.$root.userEmail
      this.loginModalElement.modal('show')
    },
    '$parent.$parent.worldStore': function (worldStore) {
      if (!this.apiStats) {
        this.stats = this.calcStats(worldStore)
      }
      this.emailConfirmReminder = this.calcEmailConfirmReminder(worldStore)
    },
  },
  destroyed: function () {
    clearInterval(this.sotwi)
    if (this.onboardingPollTimer) {
      clearInterval(this.onboardingPollTimer)
      this.onboardingPollTimer = null
    }
    if (this._onboardingReadyTimeoutId) {
      clearTimeout(this._onboardingReadyTimeoutId)
      this._onboardingReadyTimeoutId = null
    }
    if (this.$root._skIndexShellForDashboard === this) {
      this.$root._skIndexShellForDashboard = null
    }
  },
}
</script>

<style>

.componentFade-enter-active, .componentFade-leave-active {transition: opacity .2s ease;}
.componentFade-enter, .componentFade-leave-to{opacity: 0;}

.fadeDown-enter-active, .fadeInDown,
.fadeDown-leave-active, .fadeOutDown {animation-duration:.4s;animation-fill-mode:both;}
.fadeDown-enter-active, .fadeInDown  {animation-name:fadeInDown;}
.fadeDown-leave-active, .fadeOutDown {animation-name:fadeOutDown;}
.fadeLeft-enter-active, .fadeInLeft,
.fadeLeft-leave-active, .fadeOutLeft {animation-duration:.4s;animation-fill-mode:both;}
.fadeLeft-enter-active, .fadeInLeft  {animation-name:fadeInLeft;}
.fadeLeft-leave-active, .fadeOutLeft {animation-name:fadeOutLeft;}
.shiftLeft-enter-active,.shiftInLeft,
.shiftLeft-leave-active,.shiftOutLeft {animation-duration:.4s;animation-fill-mode:both;}
.shiftLeft-enter-active,.shiftInLeft  {animation-name:fadeInLeft;}
.shiftLeft-leave-active,.shiftOutLeft {animation-name:fadeOutRight;}
 .fadeRight-enter-active,  .fadeInRight,
 .fadeRight-leave-active, .fadeOutRight {animation-duration:.4s;animation-fill-mode:both;}
 .fadeRight-enter-active,  .fadeInRight  {animation-name:fadeInRight;}
 .fadeRight-leave-active, .fadeOutRight {animation-name:fadeOutRight;}
.shiftRight-enter-active, .shiftInRight,
.shiftRight-leave-active,.shiftOutRight {animation-duration:.4s;animation-fill-mode:both;}
.shiftRight-enter-active, .shiftInRight  {animation-name:fadeInRight;}
.shiftRight-leave-active,.shiftOutRight {animation-name:fadeOutLeft;}

.fromHome-enter-active,
.fromHome-leave-active,
.toHome-enter-active,
.toHome-leave-active  {animation-duration:.4s;animation-fill-mode:both;}
.fromHome-enter-active  {animation-name:fadeInLeft;}
.fromHome-leave-active  {animation-name:scaleOut;}
.toHome-enter-active  {animation-name:scaleIn;}
.toHome-leave-active  {animation-name:fadeOutLeft;}

.fromHome2-leave-active  {
  transform-origin: 50% 0%;
  -webkit-animation: rotateBottomSideFirst .8s both ease-in;
  animation: rotateBottomSideFirst .8s both ease-in;
}
@keyframes rotateBottomSideFirst {
  0% { }
  40% { -webkit-transform: rotateX(-15deg); transform: rotateX(-15deg); opacity: .8; -webkit-animation-timing-function: ease-out; animation-timing-function: ease-out; }
  100% { -webkit-transform: scale(0.8) translateZ(-200px); transform: scale(0.8) translateZ(-200px); opacity:0; }
}

.stts21kdfslds {display:none !important;}

.devNote {
  position:fixed;bottom:0;left:0; display:inline-block; background-color: #ff0; margin:4px; padding:2px; font-size: 10px; font-weight: bold; opacity:.3;
    transition: opacity .6s ease;
}
.devNote:hover {opacity:1;}
.SKannouncement {position:fixed !important; top:40px; left:100px; right:100px; margin:4px !important; opacity:.85; z-index:1001;}

#app .onboarding, .onboarding {color: #000 !important; font-weight: normal;}
#app .onboarding small, .onboarding small {color: #a33 !important; font-weight: bold;}

.white {color: #fff;}
.dwhite {color: #eee;}
</style>



// WEBPACK FOOTER //
// IndexPage.vue?f887eb7c